import type { IsolationNodeSummary } from 'features/knowledgeDocumentViewer/graph/IsolationControllerContext'

import { type LotoPointKey, toggleIsolationPoint } from './lotoIsolationPoints'
import { mergeAttachments } from './lotoReportDraft'
import type { LotoReport, LotoTargetEquipment } from './lotoReportSchemas'

function targetEquipmentLabel(target: LotoTargetEquipment): string {
  return target.tag.trim() || target.description.trim()
}

export function targetEquipmentLabels(
  targets: LotoTargetEquipment[]
): string[] {
  return [...new Set(targets.map(targetEquipmentLabel).filter(Boolean))]
}

export function targetEquipmentText(targets: LotoTargetEquipment[]): string {
  return targetEquipmentLabels(targets).join(', ')
}

function followsTargetEquipment(report: LotoReport): boolean {
  const current = report.equipment_system.trim()
  return !current || current === targetEquipmentText(report.target_equipment)
}

export function equipmentSystemText(report: LotoReport): string {
  return followsTargetEquipment(report)
    ? targetEquipmentText(report.target_equipment)
    : report.equipment_system
}

export function applyTargetEquipment(
  report: LotoReport,
  targets: LotoTargetEquipment[]
): LotoReport {
  return {
    ...report,
    target_equipment: targets,
    equipment_system: followsTargetEquipment(report)
      ? targetEquipmentText(targets)
      : report.equipment_system,
  }
}

export type LotoPickMode = 'points' | 'equipment'

export const pickModeToColor: Record<LotoPickMode, string> = {
  points: 'var(--pnid-isolation-point)',
  equipment: 'var(--pnid-target-equipment)',
}

function isTarget(target: LotoTargetEquipment, key: LotoPointKey): boolean {
  return target.node_id === key.nodeId
}

function newTargetEquipment(node: IsolationNodeSummary): LotoTargetEquipment {
  return {
    node_id: node.nodeId,
    page_id: node.pageId,
    tag: node.label,
    description: node.subtitle ?? '',
  }
}

export function removeTargetEquipment(
  report: LotoReport,
  key: LotoPointKey
): LotoReport {
  const targets = report.target_equipment.filter(
    (target) => !isTarget(target, key)
  )
  return targets.length === report.target_equipment.length
    ? report
    : applyTargetEquipment(report, targets)
}

function removeNodeIsolationPoints(
  report: LotoReport,
  key: LotoPointKey
): LotoReport {
  return {
    ...report,
    isolation_points: report.isolation_points.filter(
      (point) => point.node_id !== key.nodeId
    ),
  }
}

function toggleTargetEquipment(
  report: LotoReport,
  node: IsolationNodeSummary,
  systemDocumentId: string
): LotoReport {
  if (report.target_equipment.some((target) => isTarget(target, node)))
    return removeTargetEquipment(report, node)
  const withPage = {
    ...report,
    attachments: mergeAttachments(report.attachments, [
      { system_document_id: systemDocumentId, page_ids: [node.pageId] },
    ]),
  }
  return applyTargetEquipment(withPage, [
    ...report.target_equipment,
    newTargetEquipment(node),
  ])
}

export function toggleLotoNode(
  report: LotoReport,
  node: IsolationNodeSummary,
  systemDocumentId: string,
  mode: LotoPickMode
): LotoReport {
  if (mode === 'equipment')
    return toggleTargetEquipment(
      removeNodeIsolationPoints(report, node),
      node,
      systemDocumentId
    )
  return toggleIsolationPoint(
    removeTargetEquipment(report, node),
    node,
    systemDocumentId
  )
}
